export const LLM_CACHE_DIAGNOSTICS_STORAGE_KEY = 'meowchat:llm-cache-diagnostics'

export interface CacheUsageDiagnostic {
  totalInputTokens?: number
  cachedInputTokens?: number
}

export function mergeAnthropicUsage(
  previous: Record<string, number> | undefined,
  update: Record<string, number> | undefined,
): Record<string, number> | undefined {
  return update ? { ...previous, ...update } : previous
}

export function normalizeCompatibleCacheUsage(value: unknown): CacheUsageDiagnostic | undefined {
  if (!value || typeof value !== 'object')
    return undefined

  const usage = value as Record<string, any>
  const details = usage.prompt_tokens_details ?? usage.input_tokens_details ?? {}
  const has = (object: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(object, key)
  const inputTokens = usage.prompt_tokens ?? usage.input_tokens
  if (has(details, 'cached_tokens')) {
    return {
      totalInputTokens: inputTokens,
      cachedInputTokens: details.cached_tokens,
    }
  }

  if (has(usage, 'prompt_cache_hit_tokens')) {
    const missed = usage.prompt_cache_miss_tokens
    return {
      totalInputTokens: inputTokens ?? (typeof missed === 'number' ? usage.prompt_cache_hit_tokens + missed : undefined),
      cachedInputTokens: usage.prompt_cache_hit_tokens,
    }
  }

  if (has(usage, 'cached_content_token_count') || has(usage, 'cachedContentTokenCount')) {
    return {
      totalInputTokens: inputTokens,
      cachedInputTokens: usage.cached_content_token_count ?? usage.cachedContentTokenCount,
    }
  }

  if (has(usage, 'cache_read_input_tokens')) {
    const cached = usage.cache_read_input_tokens
    const writes = usage.cache_creation_input_tokens
    return {
      totalInputTokens: inputTokens === undefined ? undefined : inputTokens + (cached ?? 0) + (writes ?? 0),
      cachedInputTokens: cached,
    }
  }

  return { totalInputTokens: inputTokens }
}

function diagnosticsEnabled(): boolean {
  if (import.meta.env.DEV)
    return true
  try {
    return globalThis.localStorage?.getItem(LLM_CACHE_DIAGNOSTICS_STORAGE_KEY) === '1'
  }
  catch {
    return false
  }
}

export function logCacheUsage(
  provider: string,
  model: string,
  usage?: CacheUsageDiagnostic,
): void {
  if (!diagnosticsEnabled())
    return

  if (!usage) {
    console.info(`[LLM cache] ${JSON.stringify({ provider, model, status: 'usage_missing' })}`)
    return
  }

  const cached = usage.cachedInputTokens
  const total = usage.totalInputTokens
  const rate = cached !== undefined && total !== undefined && total > 0 ? (cached / total) * 100 : undefined
  const cacheHitRate = rate === undefined
    ? undefined
    : `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`
  const status = cached === undefined ? 'cache_field_missing' : cached > 0 ? 'hit' : 'miss'

  const details = {
    provider,
    model,
    status,
    cacheHitRate,
  }
  console.info(`[LLM cache] ${JSON.stringify(details)}`)
}
